import { unionWith } from "lodash";
import { mapToAppointmentModel } from "@teamprevent/common/calendar";

import { mapServerModelToAppointmentModel } from "@teamprevent/common/calendar/models";

import { BookAppointmentAction } from "../bookAppointments/actions";
import {
    BOOK_AD_HOC_APPOINTMENT_SUCCESS,
    REBOOK_APPOINTMENT_SUCCESS,
    BOOK_AD_HOC_CLINIC_APPOINTMENT_SUCCESS
} from "../bookAppointments/actionTypes";

import { HealthSurveillanceAction } from "../healthSurveillance/actions";

import {
    SUBMIT_CLINIC_HEALTH_SURVEILLANCE_CONSULTATION_SUCCESS,
    SUBMIT_CLINIC_HEALTH_SURVEILLANCE_REPEAT_CONSULTATION_SUCCESS
} from "../healthSurveillance/actionTypes";
import { ApplicationState } from "../applicationState";

import { ClinicAction } from "./clinics/actions";

import * as actionTypes from "./actionTypes";
import { CalendarAction, CalendarFilterAction } from "./actions";
import { emptyCalendarDetails, CalendarFilters } from "./models";
import {
    CREATE_TIME_NOTE_APPOINTMENT_SUCCESS,
    UPDATE_TIME_NOTE_APPOINTMENT_SUCCESS
} from "./timeNotes/actionTypes";
import { TimeNoteAppointmentAction } from "./timeNotes/actions";

type Action = CalendarAction | BookAppointmentAction | ClinicAction | HealthSurveillanceAction | TimeNoteAppointmentAction;

export function calendar(state: ApplicationState["calendar"] = { }, action: Action): ApplicationState["calendar"] {
    switch (action.type) {
        case actionTypes.LOAD_APPOINTMENTS_SUCCESS: {
            const mappedCalendars = Object.keys(action.payload).map(entityId => ({
                entityId,
                ...action.payload[entityId],
                appointments: unionWith(
                    action.payload[entityId].appointments.map(mapToAppointmentModel),
                    (state[entityId] || emptyCalendarDetails).appointments,
                    (a, b) => a.id === b.id
                )
            }));
            const mappedCalendarsObject = { ...mappedCalendars.reduce((calendars, c) => ({ ...calendars, [c.entityId]: c }), {}) };
            return {
                ...state,
                ...mappedCalendarsObject
            };
        }
        case actionTypes.LOAD_APPOINTMENT_SUCCESS: {
            const entityIds = [
                ...(action.payload.teamPreventUsers || []).map(u => u.id),
                ...(action.payload.rooms || []).map(r => r.id)
            ];
            const mappedCalendars = entityIds.map(entityId => ({
                entityId,
                ...state[entityId],
                appointments: unionWith(
                    [mapToAppointmentModel(action.payload)],
                    (state[entityId] || emptyCalendarDetails).appointments,
                    (a, b) => a.id === b.id
                )
            }));
            const mappedCalendarsObject = { ...mappedCalendars.reduce((calendars, c) => ({ ...calendars, [c.entityId]: c }), {}) };
            return {
                ...state,
                ...mappedCalendarsObject
            };
        }
        case BOOK_AD_HOC_APPOINTMENT_SUCCESS:
        case BOOK_AD_HOC_CLINIC_APPOINTMENT_SUCCESS: {
            const entityIds = [
                ...(action.payload.appointment.teamPreventUsers || []).map(u => u.id),
                ...(action.payload.appointment.rooms || []).map(r => r.id)
            ];
            const mappedCalendars = entityIds.map(entityId => ({
                entityId,
                ...state[entityId],
                appointments: unionWith(
                    [mapServerModelToAppointmentModel(action.payload.appointment)],
                    (state[entityId] || emptyCalendarDetails).appointments,
                    (a, b) => a.id === b.id
                )
            }));
            const mappedCalendarsObject = { ...mappedCalendars.reduce((calendars, c) => ({ ...calendars, [c.entityId]: c }), {}) };
            return {
                ...state,
                ...mappedCalendarsObject
            };
        }
        case CREATE_TIME_NOTE_APPOINTMENT_SUCCESS: {
            const appointment = action.payload;
            const clinicianCalendars = appointment.teamPreventUsers.reduce((calendars, c) => {
                const currentCalendar: ApplicationState["calendar"][string] = state[c.id] || { ...emptyCalendarDetails };
                return {
                    ...calendars,
                    [c.id]: { ...currentCalendar, appointments: [...currentCalendar.appointments, mapToAppointmentModel(appointment)] }
                };
            }, {});
            return { ...state, ...clinicianCalendars };
        }
        case UPDATE_TIME_NOTE_APPOINTMENT_SUCCESS: {
            return (Object.keys(state) as (keyof typeof state)[]).reduce((calendars, entityId) => {
                const currentCalendar = state[entityId];
                if (action.payload.teamPreventUsers.some(u => u.id === entityId)) {
                    if (currentCalendar.appointments.some(a => a.id === action.payload.id)) {
                        return {
                            ...calendars,
                            [entityId]: {
                                ...currentCalendar,
                                appointments: currentCalendar.appointments.map(a => a.id === action.payload.id ? mapToAppointmentModel(action.payload) : a)
                            }
                        };
                    } else {
                        return {
                            ...calendars,
                            [entityId]: {
                                ...currentCalendar,
                                appointments: [...currentCalendar.appointments, mapToAppointmentModel(action.payload)]
                            }
                        };
                    }
                }

                if (currentCalendar.appointments.some(a => a.id === action.payload.id)) {
                    return {
                        ...calendars,
                        [entityId]: { ...currentCalendar, appointments: currentCalendar.appointments.filter(a => a.id !== action.payload.id) }
                    };
                } else {
                    return {
                        ...calendars,
                        [entityId]: currentCalendar
                    };
                }
            }, {});
        }
        case actionTypes.DELETE_APPOINTMENT_SUCCESS: {
            return (Object.keys(state) as (keyof typeof state)[]).reduce((calendars, entityId) => {
                const currentCalendar = state[entityId];

                if (action.payload.clinicId && currentCalendar.appointments.some(a => a.id === action.payload.clinicId))
                {
                    return {
                        ...calendars,
                        [entityId]: {
                            ...currentCalendar,
                            appointments: currentCalendar.appointments.map(a =>
                                a.id === action.payload.clinicId
                                    ? { ...a, appointments: a.appointments!.filter(ca => ca.id !== action.payload.appointmentId) }
                                    : a
                            )
                        }
                    };
                } else if (currentCalendar.appointments.some(a => a.id === action.payload.appointmentId)) {
                    return {
                        ...calendars,
                        [entityId]: { ...currentCalendar, appointments: currentCalendar.appointments.filter(a => a.id !== action.payload.appointmentId) }
                    };
                } else {
                    return {
                        ...calendars,
                        [entityId]: currentCalendar
                    };
                }
            }, {});
        }
        case REBOOK_APPOINTMENT_SUCCESS: {
            const mappedCalendars = (Object.keys(state) as (keyof typeof state)[]).map(entityId => ({
                entityId,
                ...state[entityId],
                appointments: state[entityId].appointments.filter(a => a.id !== action.payload.cancelledAppointmentId)
            }));
            return { ...mappedCalendars.reduce((calendars, c) => ({ ...calendars, [c.entityId]: c }), {}) };
        }
        case SUBMIT_CLINIC_HEALTH_SURVEILLANCE_CONSULTATION_SUCCESS:
        case SUBMIT_CLINIC_HEALTH_SURVEILLANCE_REPEAT_CONSULTATION_SUCCESS: {
            const mappedCalendars = (Object.keys(state) as (keyof typeof state)[]).map(entityId => ({
                entityId,
                ...state[entityId],
                appointments: state[entityId].appointments.map(c => {
                    if (c.id === action.payload.clinicId) {
                        return {
                            ...c,
                            appointments: c.appointments!.map(a => {
                                if (a.id === action.payload.appointmentId) {
                                    return {
                                        ...a,
                                        isClinicalNotesComplete: true
                                    };
                                }

                                return a;
                            })
                        };
                    }

                    return c;
                })
            }));
            return { ...mappedCalendars.reduce((calendars, c) => ({ ...calendars, [c.entityId]: c }), {}) };
        }
        default:
            return state;
    }
}

export const calendarFilters = (state: CalendarFilters = { userIds: [], roomIds: [] }, action: CalendarFilterAction): CalendarFilters => {
    switch (action.type) {
        case actionTypes.UPDATE_CALENDAR_SELECTED_USER_IDS:
            return {
                ...state,
                userIds: action.payload
            };
        case actionTypes.UPDATE_CALENDAR_SELECTED_ROOM_IDS:
            return {
                ...state,
                roomIds: action.payload
            };
        default:
            return state;
    }
};
